import type { ButtonHTMLAttributes, ReactNode } from 'react';

// Botón base del sistema de diseño. Alturas fijadas por policies/ux-ui-design.md §4:
// `h-10` secundario, `h-12` primario. El foco visible nunca se quita sin reemplazo (§7.2 regla 2):
// todas las variantes llevan `focus-visible:ring-*`.

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-zinc-900 text-white shadow-lg shadow-zinc-900/10 hover:bg-zinc-800 hover:shadow-xl '
    + 'focus-visible:ring-zinc-900 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100 dark:focus-visible:ring-white',
  secondary:
    'bg-white/80 text-zinc-800 border border-zinc-200/80 backdrop-blur-xl shadow-sm hover:bg-white hover:shadow-md '
    + 'focus-visible:ring-zinc-400 dark:bg-zinc-900/70 dark:text-zinc-100 dark:border-zinc-700/70 dark:hover:bg-zinc-900',
  ghost:
    'bg-transparent text-zinc-600 hover:bg-zinc-900/5 hover:text-zinc-900 focus-visible:ring-zinc-400 '
    + 'dark:text-zinc-400 dark:hover:bg-white/10 dark:hover:text-zinc-100',
  // El color NO es el único aviso de que un botón destruye: el texto lo dice y, en las acciones de
  // consecuencia, además hay que tipear el nombre del objeto (ConfirmDangerModal).
  danger:
    'bg-rose-600 text-white shadow-lg shadow-rose-600/20 hover:bg-rose-700 focus-visible:ring-rose-500 '
    + 'dark:bg-rose-600 dark:hover:bg-rose-500',
};

const SIZES: Record<ButtonSize, string> = {
  md: 'h-10 px-4 text-sm rounded-xl gap-2',
  lg: 'h-12 px-6 text-base rounded-2xl gap-2.5',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Deshabilita y muestra el spinner. El texto sigue siendo el mismo: nada de «Cargando…» mudo. */
  loading?: boolean;
  leadingIcon?: ReactNode;
  fullWidth?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  leadingIcon,
  fullWidth = false,
  className = '',
  disabled,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      // `aria-busy` para que un lector de pantalla sepa que la acción está en curso; el `disabled`
      // solo no comunica nada.
      aria-busy={loading || undefined}
      className={[
        'inline-flex items-center justify-center font-medium transition-all duration-300',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        'focus-visible:ring-offset-zinc-50 dark:focus-visible:ring-offset-zinc-950',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none',
        VARIANTS[variant],
        SIZES[size],
        fullWidth ? 'w-full' : '',
        className,
      ].filter(Boolean).join(' ')}
      {...rest}
    >
      {loading ? (
        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
          <path className="opacity-90" fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-3a7 7 0 0 0-7-7V2Z" />
        </svg>
      ) : leadingIcon ? (
        <span aria-hidden="true" className="inline-flex">{leadingIcon}</span>
      ) : null}
      {children}
    </button>
  );
}
